import { createRouteRef } from '@backstage/core-plugin-api';

export const rootRouteRef = createRouteRef({
  id: 'security-insights',
});

/** Valid URL search params for each sub-route. Used by SecurityNav to filter which params carry across tab navigation. */
export const validParamsByPath: Record<string, ReadonlySet<string>> = {
  '': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-asset': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-issue-type': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-cve': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-pillar': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-category': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),
  'by-pillar-category': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
  ]),

  // Drill state (bu/xp/rpillar) plus the shared single-view filter params.
  'posture-report': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'pillar',
    'category',
    'cve',
    'sloCheck',
    'isUnique',
    'isQuiesced',
    'bu',
    'xp',
    'rteam',
    'rpillar',
    'rcategory',
    'issues',
  ]),
  reports: new Set(['experience', 'team', 'org', 'firstDate', 'lastDate']),
  baseline: new Set([]),
  cspm: new Set([
    'tab',
    'orca_view',
    'cs_account',
    'cs_region',
    'cs_exp',
    'cs_team',
    'cs_from',
    'cs_to',
    'cs_severities',
    'orca_exp',
    'orca_team',
    'orca_provider',
    'orca_account',
    'orca_from',
    'orca_to',
    'orca_severities',
    'f_account',
    'f_status',
    'f_team',
  ]),
  // `sc_`-prefixed so the tab's own state can't collide with params other
  // tabs carry across navigation (the compliance tab uses `c_`).
  'security-controls': new Set([
    'sc_tab',
    'sc_exp',
    'sc_app',
    'sc_drill',
    // An expanded instance row's drill-downs: `sc_drill_inst` narrows the open
    // `sc_drill` System's alerts to one deployed instance, `sc_find_inst`
    // narrows the Taxonomy Matrix's `sc_find_app` findings the same way.
    'sc_drill_inst',
    'sc_find_inst',
    'sc_control',
    'sc_status',
    'sc_remediation',
    // Attestation Form tab's own Control/Team/System selection — separate
    // from sc_control/sc_app/sc_drill, which already drive the Controls
    // Matrix drill-down and must stay independent of this tab's selection.
    'sc_att_control',
    'sc_att_team',
    'sc_att_system',
  ]),
  compliance: new Set([
    'c_framework',
    'c_team',
    'c_control',
    'c_category',
    'c_status',
    'c_remediation',
  ]),
  hackerone: new Set(['h1_asset', 'h1_from', 'h1_to', 'h1_severities']),
  xmco: new Set([
    'xmco_asset',
    'xmco_status',
    'xmco_exp',
    'xmco_team',
    'xmco_from',
    'xmco_to',
    'xmco_severities',
  ]),
  'license-compliance': new Set([
    'experience',
    'team',
    'severities',
    'issueType',
    'license',
    'sloCheck',
    'isUnique',
    'isQuiesced',
    'view',
  ]),
  'domains-verification': new Set(['owner', 'search']),
  'ghas-rollout': new Set([
    'tab',
    'experience',
    'team',
    'org',
    'compliance',
    'requirement',
  ]),
  scorecard: new Set([
    'tab',
    'company',
    'factor',
    'issueType',
    'severities',
    'groupStatus',
    'team',
    'search',
    'page',
    'pageSize',
  ]),
  'github-actions': new Set([
    'tab',
    'org',
    'experience',
    'team',
    'languages',
    'excludeRepos',
    'missingOnly',
  ]),
  'elt-vulnerabilities': new Set([
    'granularity',
    'date',
    'from',
    'to',
    'sort',
    'bu',
    'xp',
  ]),
  'slo-trend': new Set(['date', 'sort', 'expand']),
  status: new Set([]),
  schema: new Set([]),
  // Reason-centric report (v2) — 'experience'/'severities'/'tool'/'page'/
  // 'pageSize' were valid for the old per-finding paginated table this page
  // used to render; the report has no per-finding filters or pagination.
  'quiesced-findings': new Set(['team', 'status', 'asset']),
  // The Falcon system drill-down (SEC-1765). Every entry here answers "which
  // search params does this tab accept when SecurityNav carries them across a
  // tab change", per the note on the table above. This route accepts none: its
  // subject travels in the path (`/falcon/:team/:system`), not in prefixed
  // query params like every other drill-down in this plugin, and `team` and
  // `system` are therefore not the kind of param this table can describe.
  //
  // Inert today, unlike `baseline`'s identical empty set: SecurityNav only
  // looks up paths that are in `subRoutes`, and `falcon` is deliberately not
  // one of them (see the note on `subRoutes` below). It is recorded here so
  // that SEC-1763, which adds the tab, finds the answer already written down
  // rather than reading the gap as an oversight.
  falcon: new Set([]),
};

/**
 * Shared sub-route definitions consumed by both Router and SecurityNav.
 *
 * `falcon` is deliberately absent. The Falcon system drill-down is a route,
 * not a tab: it is always about one specific System, so there is nothing for
 * a tab to land on. Registering the Falcon Compliance *tab* — with an index
 * page to drill down from — is SEC-1763's job, and that ticket is marked Done
 * in Jira but has never landed in this repo. See `Router.tsx`.
 */
export const subRoutes = [
  { path: '', label: 'Single View' },
  { path: 'posture-report', label: 'Posture Report' },
  { path: 'reports', label: 'GitHub Reports' },
  { path: 'baseline', label: 'Repository Hygiene Baseline Score' },
  { path: 'cspm', label: 'CSPM' },
  {
    path: 'security-controls',
    label: 'Security Controls By Application',
  },
  { path: 'compliance', label: 'Application Security Posture', hidden: true },
  { path: 'hackerone', label: 'HackerOne' },
  { path: 'xmco', label: 'XMCO' },
  { path: 'license-compliance', label: 'License Compliance' },
  { path: 'domains-verification', label: 'Domains' },
  { path: 'ghas-rollout', label: 'GHAS Rollout' },
  { path: 'scorecard', label: 'Domain Security (Security Scorecard source)' },
  { path: 'github-actions', label: 'GitHub Actions' },
  { path: 'elt-vulnerabilities', label: 'ELT Dashboard' },
  { path: 'slo-trend', label: 'SLO Achievement' },
  { path: 'status', label: 'Status' },
  { path: 'schema', label: 'Schema' },
  { path: 'quiesced-findings', label: 'Quiesced Findings' },
] as const;

export type SubRoutePath = (typeof subRoutes)[number]['path'];
